import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Avatar,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import { CargoCompanyUser } from "../../../types/cargoCompany";

interface CargoCompanyUsersTableProps {
  users: CargoCompanyUser[];
  isLoading: boolean;
  error: any;
  onRemove: (user: CargoCompanyUser) => void;
}

const CargoCompanyUsersTable: React.FC<CargoCompanyUsersTableProps> = ({
  users,
  isLoading,
  error,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    userId: string,
  ) => {
    setAnchorEl({ ...anchorEl, [userId]: event.currentTarget });
  };

  const handleMenuClose = (userId: string) => {
    setAnchorEl({ ...anchorEl, [userId]: null });
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t("admin.cargoCompanies.failedToLoadUsers")}
      </Alert>
    );
  }

  if (isLoading) {
    return <LinearProgress />;
  }

  if (users.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <PersonIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {t("admin.cargoCompanies.noUsersAssignedToCompany")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("admin.cargoCompanies.user")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.email")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.status")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.assignedDate")}</TableCell>
            <TableCell align="right">{t("admin.cargoCompanies.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.userId} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {user.userFirstName?.[0]}
                    {user.userLastName?.[0]}
                  </Avatar>
                  <Typography variant="body2" fontWeight="medium">
                    {user.userFirstName} {user.userLastName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{user.userEmail}</TableCell>
              <TableCell>
                <Chip
                  label={user.isActive ? t("admin.cargoCompanies.active") : t("admin.cargoCompanies.inactive")}
                  color={user.isActive ? "success" : "default"}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {new Date(user.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, user.userId)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl[user.userId]}
                  open={Boolean(anchorEl[user.userId])}
                  onClose={() => handleMenuClose(user.userId)}
                >
                  <MenuItem
                    onClick={() => {
                      onRemove(user);
                      handleMenuClose(user.userId);
                    }}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("admin.cargoCompanies.removeUser")}
                  </MenuItem>
                </Menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CargoCompanyUsersTable;

